import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { AutocompleteSelect } from '@/components/Autocomplete';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';
import { buildGeneratedCampaignName, getCampaignIndustrySegment } from '../lib/campaign-name';
import {
  fetchDfsLanguagesForCountry,
  searchDfsCountries,
  type DfsLocation,
} from '../lib/dataforseo';
import { useWizardStore, type Language } from '../lib/wizard-store';
import { GenerateDescriptionButton, SuggestOffersPanel } from './W2_AiAssistants';

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isValidUrl(value: string): boolean {
  if (!value) return true;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export function W2_SenderOfferTarget() {
  const store = useWizardStore();
  const hasFormfill = store.channels_enabled.includes('formfill');

  const { data: languagesData } = useQuery({
    queryKey: ['dfs-languages', store.target_country],
    queryFn: () => fetchDfsLanguagesForCountry(store.target_country),
    staleTime: 60 * 60 * 1000,
    enabled: !!store.target_country,
  });
  const languages = useMemo(() => languagesData ?? [], [languagesData]);

  useEffect(() => {
    if (languages.length === 0) return;
    if (!languages.some((language) => language.code === store.language)) {
      store.patchW2({ language: languages[0]!.code as Language } as never);
    }
  }, [languages, store.language, store.patchW2]);

  const patch = (key: string, value: string) => store.patchW2({ [key]: value } as never);
  const generatedNamePreview = buildGeneratedCampaignName({
    targetCountry: store.target_country,
    language: store.language,
    leadscrapeIndustries: store.leadscrape_targeting.industries,
    preferredSectors: store.fit_gate.preferred_sectors,
    campaignIdentifier: store.campaign_identifier,
    offerTitle: store.offer_title,
    senderCompanyName: store.sender_company_name,
  });
  const industrySegment = getCampaignIndustrySegment({
    targetCountry: store.target_country,
    language: store.language,
    leadscrapeIndustries: store.leadscrape_targeting.industries,
    preferredSectors: store.fit_gate.preferred_sectors,
  });

  const emailError =
    store.sender_email && !isValidEmail(store.sender_email) ? 'Format email invalide' : null;
  const senderWebsiteError =
    store.sender_website && !isValidUrl(store.sender_website) ? 'Format URL invalide' : null;
  const offerUrlError = store.offer_url && !isValidUrl(store.offer_url) ? 'Format URL invalide' : null;

  function onCountrySelected(_id: string, item: DfsLocation | null) {
    if (!item) {
      store.patchW2({
        target_country: '',
        target_country_dfs_code: null,
        target_region: '',
        target_region_dfs_code: null,
      } as never);
      return;
    }
    store.patchW2({
      target_country: item.iso,
      target_country_dfs_code: item.code,
      target_region: '',
      target_region_dfs_code: null,
    } as never);
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <Card>
          <CardHeader
            title="Contexte expediteur"
            subtitle="Qui envoie, depuis quel contexte, et avec quelles informations de base."
          />
          <CardContent className="space-y-4" data-testid="sender-section">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="sender_company_name">Entreprise *</Label>
                <Input
                  id="sender_company_name"
                  value={store.sender_company_name}
                  onChange={(e) => patch('sender_company_name', e.target.value)}
                  placeholder="Acme SAS"
                  data-testid="sender-company-name"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sender_website">Site web</Label>
                <Input
                  id="sender_website"
                  value={store.sender_website}
                  onChange={(e) => patch('sender_website', e.target.value)}
                  placeholder="https://acme.com"
                  data-testid="sender-website"
                />
                {senderWebsiteError && (
                  <p className="text-xs text-red-500" data-testid="sender-website-error">
                    {senderWebsiteError}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sender_description">Description entreprise</Label>
              <Textarea
                id="sender_description"
                value={store.sender_description}
                onChange={(e) => patch('sender_description', e.target.value)}
                placeholder="Courte presentation de votre activite..."
                rows={3}
              />
              <GenerateDescriptionButton
                websiteUrl={store.sender_website}
                companyName={store.sender_company_name}
                language={store.language}
                onGenerated={(description) => patch('sender_description', description)}
              />
            </div>

            {hasFormfill && (
              <div className="grid gap-4 md:grid-cols-3" data-testid="sender-formfill-identity">
                <div className="space-y-1">
                  <Label htmlFor="sender_first_name">Prenom *</Label>
                  <Input
                    id="sender_first_name"
                    value={store.sender_first_name}
                    onChange={(e) => patch('sender_first_name', e.target.value)}
                    placeholder="Jean"
                    data-testid="sender-first-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sender_role">Role</Label>
                  <Input
                    id="sender_role"
                    value={store.sender_role}
                    onChange={(e) => patch('sender_role', e.target.value)}
                    placeholder="Directeur commercial"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sender_email">Email expediteur *</Label>
                  <Input
                    id="sender_email"
                    type="email"
                    value={store.sender_email}
                    onChange={(e) => patch('sender_email', e.target.value)}
                    placeholder="jean@acme.com"
                    data-testid="sender-email"
                  />
                  {emailError && (
                    <p className="text-xs text-red-500" data-testid="email-error">
                      {emailError}
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Cadre campagne"
            subtitle="Le socle qui cadence le naming, le pays DataForSEO, et l'identifiant de campagne."
          />
          <CardContent className="space-y-4" data-testid="campaign-frame-section">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="target_country">Pays *</Label>
                <AutocompleteSelect<DfsLocation>
                  value={store.target_country}
                  initialLabel={store.target_country ? `${store.target_country} - ${store.target_country}` : ''}
                  onSelect={onCountrySelected}
                  onSearch={searchDfsCountries}
                  renderItem={(location) => `${location.iso} - ${location.name}`}
                  getId={(location) => location.iso}
                  placeholder="Rechercher un pays..."
                  queryKey="dfs-countries-w2"
                  data-testid="target-country"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="language">Langue</Label>
                <Select
                  id="language"
                  value={store.language}
                  onChange={(event) => store.patchW2({ language: event.target.value } as never)}
                  data-testid="language"
                  disabled={!store.target_country || languages.length === 0}
                >
                  {!store.target_country && <option value="">Selectionne un pays d'abord</option>}
                  {store.target_country && languages.length === 0 && (
                    <option value="">Chargement des langues...</option>
                  )}
                  {languages.map((language) => (
                    <option key={language.code} value={language.code}>
                      {language.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="campaign_identifier">Identifiant campagne</Label>
                <Input
                  id="campaign_identifier"
                  value={store.campaign_identifier}
                  onChange={(e) => patch('campaign_identifier', e.target.value)}
                  placeholder="industry-campaign-id"
                  data-testid="campaign-identifier"
                />
                <p className="text-xs text-gray-500">
                  Si tu le laisses vide, le titre de l'offre servira de base.
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="campaign-name-preview">Apercu du nom synchronise</Label>
                <Input
                  id="campaign-name-preview"
                  value={generatedNamePreview}
                  readOnly
                  className="bg-gray-50 text-gray-700"
                  data-testid="campaign-name-preview"
                />
                <p className="text-xs text-gray-500">
                  Pattern auto: <span className="font-medium">CH_FR-industry-campaign-id-brand</span>
                </p>
              </div>
            </div>

            <div className="rounded-brand border border-dashed border-brand-border bg-gray-50 px-4 py-3 text-sm text-gray-600">
              <p className="font-medium text-gray-800">Segments utilises</p>
              <div className="mt-2 grid gap-2 md:grid-cols-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400">Pays + langue</p>
                  <p>{store.target_country && store.language ? `${store.target_country}_${store.language.toUpperCase()}` : '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400">Industry</p>
                  <p>{industrySegment || 'A definir plus tard dans le ciblage'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400">Campaign id</p>
                  <p>{store.campaign_identifier || store.offer_title || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400">Brand</p>
                  <p>{store.sender_company_name || '-'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader title="Offre" subtitle="Ce que tu proposes, de facon simple et actionnable." />
        <CardContent className="space-y-4" data-testid="offer-section">
          <SuggestOffersPanel
            companyName={store.sender_company_name}
            companyDescription={store.sender_description}
            websiteUrl={store.sender_website}
            language={store.language}
            onPick={(offer) =>
              store.patchW2({
                offer_title: offer.title,
                offer_description: offer.description,
              } as never)
            }
          />
          <div className="grid gap-4 xl:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
            <div className="space-y-1">
              <Label htmlFor="offer_title">Titre de l'offre</Label>
              <Input
                id="offer_title"
                value={store.offer_title}
                onChange={(e) => patch('offer_title', e.target.value)}
                placeholder="Audit SEO gratuit"
                data-testid="offer-title"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="offer_url">URL de l'offre</Label>
              <Input
                id="offer_url"
                value={store.offer_url}
                onChange={(e) => patch('offer_url', e.target.value)}
                placeholder="https://acme.com/offre"
                data-testid="offer-url"
              />
              {offerUrlError && (
                <p className="text-xs text-red-500" data-testid="offer-url-error">
                  {offerUrlError}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="offer_description">Description de l'offre</Label>
            <Textarea
              id="offer_description"
              value={store.offer_description}
              onChange={(e) => patch('offer_description', e.target.value)}
              placeholder="Decris ton offre en quelques lignes..."
              rows={4}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
